import { TColumnType } from 'components/Base/Table/Table.types';
import Button from 'components/Base/Button/Button';
import React from 'react';
import useGetTableColumnData from 'components/Base/Table/components/SearchFilter';
import CalendarDatePicker from 'components/Shared/CalendarDatePicker/CalendarDatePicker';
import { TTask } from 'data/types/pillarsActionPlan.types';
import {
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useUpdateTaskMutation,
} from 'redux/apiSlice/pillarsActionPlan.slice';
import { ColumnFilterItem } from 'antd/es/table/interface';
import { StatusType } from '../TacticTable.types';
import { DeleteTrashOutline } from '../../../../../../assets/icon/icon';
import TaskStatusDropdown from './TaskStatusDropdown/TaskStatusDropdown';
import TaskAssigneeDropdown from './TaskAsigneeDropdown/TaskAsigneeDropdown';
import TaskTitleInput from './TaskTitleInput/TaskTitleInput';
import { compareTaskDueDates } from './TaskTable.helpers';

export const useGetColumnsData = (
  actionPlanId: number,
  onTaskSaved?: (tempRowId: string, newTask: TTask) => void,
  onTaskCancelled?: (rowId: string) => void,
  assigneeFilters?: ColumnFilterItem[],
  isPreviousPlan?: boolean,
  isAllElcs?: boolean,
) => {
  const { getTableColumnSearchProps } = useGetTableColumnData();

  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();

  const isMountedRef = React.useRef(true);
  React.useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const getDeleteTaskTooltip = (): string => {
    if (isPreviousPlan) {
      return 'This tactic is from a previous plan and cannot be modified';
    }
    if (isAllElcs) {
      return 'Deleting tasks is disabled on the global page';
    }
    return 'Remove';
  };

  const onSaveTask = async (rowId: string, taskTitle: string) => {
    if (!taskTitle.trim()) {
      onTaskCancelled?.(rowId);
      return;
    }

    const newTask = await createTask({
      actionPlanId,
      taskTitle,
    }).unwrap();

    if (isMountedRef.current) {
      onTaskSaved?.(rowId, newTask);
    }
  };

  const onDeleteTask = async (taskId: string) => {
    await deleteTask({ taskId: Number(taskId) });
  };

  const onUpdateTask = async (taskId: string, field: string, value: string) => {
    await updateTask({
      taskId: Number(taskId),
      field,
      value,
    }).unwrap();
  };

  const defaultColumns: TColumnType<TTask>[] = [
    {
      title: ' ',
      dataIndex: '_',
      key: '_',
      width: 50,
    },
    {
      title: 'Task',
      dataIndex: 'taskTitle',
      key: 'taskTitle',
      render: (taskTitle: string, record: TTask) => (
        <div data-cy="task_title_input">
          <TaskTitleInput
            value={taskTitle}
            rowId={record.taskId || record.rowId}
            isNewRow={!record.taskId && !!record.rowId}
            disabled={isPreviousPlan || isAllElcs}
            onSave={(_, title: string) => {
              if (record.taskId) {
                onUpdateTask?.(record.taskId, 'NAME', title);
              }
              if (record.rowId) {
                onSaveTask?.(record.rowId, title);
              }
            }}
            onCancel={onTaskCancelled}
          />
        </div>
      ),
      ...getTableColumnSearchProps<TTask>(),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 220,
      render: (dueDate: string, record: TTask) => (
        <div data-cy="task_due_date_input">
          <CalendarDatePicker
            value={dueDate}
            disabled={
              isPreviousPlan || isAllElcs || record?.taskTitle?.trim() === ''
            }
            placeholder="Add Date"
            onDateChange={date => {
              if (!date || !record.taskId) return;

              onUpdateTask?.(record.taskId, 'DUE_DATE', date);
            }}
          />
        </div>
      ),
      // Undated tasks stay last in both directions (GROW-4272)
      sorter: compareTaskDueDates,
    },
    {
      title: 'Assignee',
      dataIndex: 'assignee',
      key: 'assignee',
      width: 220,
      render: (assignee: string, record: TTask) => (
        <div data-cy="task_assignee_select">
          <TaskAssigneeDropdown
            value={assignee}
            disabled={
              isPreviousPlan || isAllElcs || record?.taskTitle?.trim() === ''
            }
            onAssigneeChange={assignTo => {
              if (!assignTo || !record.taskId) return;

              onUpdateTask?.(record.taskId, 'ASSIGNEE', assignTo);
            }}
          />
        </div>
      ),
      filters: assigneeFilters,
      onFilter: (value, record) => record.assignee === value,
    },
    {
      title: 'Task Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status: StatusType, record: TTask) => (
        <div data-cy="task_status_dropdown">
          <TaskStatusDropdown
            task={record}
            value={status}
            disabled={
              isPreviousPlan || isAllElcs || record?.taskTitle?.trim() === ''
            }
          />
        </div>
      ),
      filters: [
        { text: 'To Do', value: 'TODO' },
        { text: 'In Progress', value: 'IN_PROGRESS' },
        { text: 'Stalled', value: 'STALLED' },
        { text: 'Completed', value: 'COMPLETED' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'end',
      width: 100,
      render: (_: any, record: TTask) => (
        <div data-cy="task_delete_btn">
          <Button
            variant="ghost"
            onClick={async () => {
              if (record.taskId) {
                await onDeleteTask?.(record.taskId);
              } else if (record.rowId) {
                onTaskCancelled?.(record?.rowId);
              }
            }}
            aria-label="Delete Task"
            disabled={isPreviousPlan || isAllElcs}
            tooltip={getDeleteTaskTooltip()}
          >
            <DeleteTrashOutline
              size={16}
              fill={isPreviousPlan || isAllElcs ? '#B5B5B5' : undefined}
            />
          </Button>
        </div>
      ),
    },
  ];

  return { defaultColumns };
};
